// src/components/Pages/Services.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getPageBySlug } from '../../helpers/Api';
import Header from '../Header';
import Section from '../Section';
import BannerSectionStyle5 from '../Section/BannerSection/BannerSectionStyle5';
import AboutSectionStyle2 from '../Section/AboutSection/AboutSectionStyle2';
import DepartmentSectionStyle4 from '../Section/DepartmentSection/DepartmentSectionStyle4';
import FormStyle1 from '../Form/FormStyle1';
import FormStyle2 from '../Form/FormStyle2';
import FormStyle3 from '../Form/FormStyle3';
import FormStyle4 from '../Form/FormStyle4';
import FormStyle5 from '../Form/FormStyle5';
import FormStyle6 from '../Form/FormStyle6';
import FormStyle7 from '../Form/FormStyle7';
import FormStyle8 from '../Form/FormStyle8';
import FormStyle9 from '../Form/FormStyle9';
import FormStyle10 from '../Form/FormStyle10';
import ContactMethods from '../ContactMethods/ContactMethods';

import Accordion from '../Accordion';
import ReactDOMServer from 'react-dom/server';
import parse from 'html-react-parser';
import languages from '../../config/languages';
import { TranslationContext } from '../../contexts/TranslationContext';

// Función para obtener la base URL
const getBaseUrl = () => 'https://dashboard.gestlifesurrogacy.com';

// Función para obtener una imagen de respaldo si la original no está disponible
const getFallbackImage = (type) => {
  switch (type) {
    case 'bg':
      return '/images/fallback-bg.webp';
    case 'img':
      return '/images/fallback-img.webp';
    default:
      return '/images/default.webp';
  }
};

// Procesar contenido HTML para actualizar rutas de imágenes y agregar lazy loading
const processHtmlContent = (html, baseUrl) => {
  if (typeof document === 'undefined') return html;

  const div = document.createElement('div');
  div.innerHTML = html;
  const images = div.getElementsByTagName('img');

  for (let img of images) {
    const src = img.getAttribute('src');
    if (src && src.startsWith('/uploads')) {
      img.setAttribute('src', baseUrl + src);
    }
    img.setAttribute('loading', 'lazy');
  }

  return div.innerHTML;
};

// Funciones para reemplazar shortcodes con componentes React
const replaceAboutSection = (html) => {
  const aboutSectionRegex = /--about-section title="([^"]+)" subTitle="([^"]+)" imgUrl="([^"]+)"--/g;
  return html.replace(aboutSectionRegex, (match, title, subTitle, imgUrl) => {
    const aboutSectionComponent = (
      <AboutSectionStyle2
        title={title}
        subTitle={subTitle}
        imgUrl={imgUrl}
      />
    );
    return ReactDOMServer.renderToString(aboutSectionComponent);
  });
};

const replaceDepartmentSection = (html) => {
  const departmentSectionRegex = /--department-section sectionTitle="([^"]+)" sectionTitleUp="([^"]+)" data='([^']+)'--/g;
  return html.replace(departmentSectionRegex, (match, sectionTitle, sectionTitleUp, data) => {
    const parsedData = JSON.parse(data);
    return `<div data-component="DepartmentSectionStyle4" data-props='${JSON.stringify({
      sectionTitle,
      sectionTitleUp,
      data: parsedData,
    })}'></div>`;
  });
};

const replaceFormStyle1 = (html) => {
  const formStyle1Regex = /--form-style-1--/g;
  return html.replace(formStyle1Regex, () => `<div data-component='FormStyle1'></div>`);
};

const replaceFormStyle2 = (html) => {
  const formStyle2Regex = /--form-style-2--/g;
  return html.replace(formStyle2Regex, () => `<div data-component='FormStyle2'></div>`);
};

const replaceFormStyle3 = (html) => {
  const formStyle3Regex = /--form-style-3--/g;
  return html.replace(formStyle3Regex, () => `<div data-component='FormStyle3'></div>`);
};

const replaceFormStyle4 = (html) => {
  const formStyle4Regex = /--form-style-4--/g;
  return html.replace(formStyle4Regex, () => `<div data-component='FormStyle4'></div>`);
};

const replaceFormStyle5 = (html) => {
  const formStyle5Regex = /--form-style-5--/g;
  return html.replace(formStyle5Regex, () => `<div data-component='FormStyle5'></div>`);
};

const replaceFormStyle6 = (html) => {
  const formStyle6Regex = /--form-style-6--/g;
  return html.replace(formStyle6Regex, () => `<div data-component='FormStyle6'></div>`);
};

const replaceFormStyle7 = (html) => {
  const formStyle7Regex = /--form-style-7--/g;
  return html.replace(formStyle7Regex, () => `<div data-component='FormStyle7'></div>`);
};

const replaceFormStyle8 = (html) => {
  const formStyle8Regex = /--form-style-8--/g;
  return html.replace(formStyle8Regex, () => `<div data-component='FormStyle8'></div>`);
};

const replaceFormStyle9 = (html) => {
  const formStyle9Regex = /--form-style-9--/g;
  return html.replace(formStyle9Regex, () => `<div data-component='FormStyle9'></div>`);
};

const replaceFormStyle10 = (html) => {
  const formStyle10Regex = /--form-style-10--/g;
  return html.replace(formStyle10Regex, () => `<div data-component='FormStyle10'></div>`);
};

// Variable global para acumular las FAQs de los acordeones
let aggregatedFaqItems = [];

const replaceAccordion = (html) => {
  const accordionRegex = /--accordion\[(\[[\s\S]*?\])\]accordion--/g;

  return html.replace(accordionRegex, (match, jsonData) => {
    try {
      const parsedData = JSON.parse(jsonData.trim()).map((item) => ({
        question: item.question,
        answer: item.answer,
      }));

      // Acumulamos las FAQs de este acordeón
      aggregatedFaqItems.push(...parsedData);

      // Preparamos las propiedades para el componente Accordion
      const props = JSON.stringify({
        data: parsedData.map((item) => ({
          title: item.question,
          content: item.answer,
        })),
        variant: 'cs_accordians text-color-grey cs_style1',
      }).replace(/'/g, '&#39;');

      // Retornamos el componente Accordion sin inyectar el schema aquí
      return `<div data-component='Accordion' data-props='${props}'></div>`;
    } catch (error) {
      console.error('Error parsing accordion JSON:', error);
      return `<div class="accordion-error">Error procesando el acordeón. Por favor, verifica el formato del JSON.</div>`;
    }
  });
};

const replaceShortcodesWithComponents = (html) => {
  // Reiniciamos el acumulador cada vez que procesamos el contenido
  aggregatedFaqItems = [];

  html = replaceAboutSection(html);
  html = replaceDepartmentSection(html);
  html = replaceFormStyle1(html);
  html = replaceFormStyle2(html);
  html = replaceFormStyle3(html);
  html = replaceFormStyle4(html);
  html = replaceFormStyle5(html);
  html = replaceFormStyle6(html);
  html = replaceFormStyle7(html);
  html = replaceFormStyle8(html);
  html = replaceFormStyle9(html);
  html = replaceFormStyle10(html);
  html = replaceAccordion(html);

  // Inyectamos un único bloque JSON-LD consolidado para FAQs si existen
  if (aggregatedFaqItems.length > 0) {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": aggregatedFaqItems.map((item) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer,
        },
      })),
    };

    html += `<script type="application/ld+json">${JSON.stringify(faqSchema)}</script>`;
  }

  return html;
};

// Inyecta el schema de Service usando metaTitle y metaDescription
const injectServiceSchema = (html, metaTitle, metaDescription) => {
  /*const organizationName = "GestLife Surrogacy";
  const organizationUrl = "https://dashboard.gestlifesurrogacy.com";
  
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": metaTitle,
    "description": metaDescription,
    "provider": {
      "@type": "Organization",
      "name": organizationName,
      "url": organizationUrl
    },
    "areaServed": {
      "@type": "Country",
      "name": "Global"
    }
  };*/

  //return html + `<script type="application/ld+json">${JSON.stringify(serviceSchema)}</script>`;
  return html;
};

// Inyecta el schema de WebPage usando metaTitle, metaDescription y la URL de la página
const injectWebPageSchema = (html, metaTitle, metaDescription, pageUrl) => {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": metaTitle,
    "description": metaDescription,
    "url": pageUrl
  };

  return html + `<script type="application/ld+json">${JSON.stringify(webPageSchema)}</script>`;
};

const isExternalLink = (url) => {
  try {
    const linkDomain = new URL(url, 'https://dashboard.gestlifesurrogacy.com').hostname;
    // Si el dominio no termina en "gestlifesurrogacy.com" lo consideramos externo.
    return !linkDomain.endsWith('gestlifesurrogacy.com');
  } catch (e) {
    return true;
  }
};

const renderComponents = (html) => {
  return parse(html, {
    replace: (domNode) => {
      // Para enlaces <a>, agregamos rel="nofollow" si son externos
      if (domNode.name === 'a' && domNode.attribs && domNode.attribs.href) {
        const hrefValue = domNode.attribs.href;
        if (isExternalLink(hrefValue)) {
          domNode.attribs.rel = 'nofollow';
        }
      }

      if (domNode.attribs && domNode.attribs['data-component']) {
        const Component = {
          DepartmentSectionStyle4,
          FormStyle1,
          FormStyle2,
          FormStyle3,
          FormStyle4,
          FormStyle5,
          AboutSectionStyle2,
          Accordion,
          FormStyle6,
          FormStyle7,
          FormStyle8,
          FormStyle9,
          FormStyle10
        }[domNode.attribs['data-component']];

        if (Component) {
          let props = {};
          if (domNode.attribs['data-props']) {
            try {
              props = JSON.parse(domNode.attribs['data-props']);
            } catch (error) {
              console.error(
                `Error parsing props for component ${domNode.attribs['data-component']}:`,
                error
              );
            }
          }

          return (
            <Section bottomMd={125} bottomLg={0} bottomXl={85}>
              <Component {...props} />
            </Section>
          );
        }
      }
    },
  });
};

const Services = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const baseUrl = getBaseUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const languageCode = location.pathname.split('/')[1];
  const [canonicalUrl, setCanonicalUrl] = useState('');

  // Verificar si el primer segmento de la ruta es un código de idioma válido
  const language = languages[languageCode] ? languageCode : 'es';
  const { setTranslations } = useContext(TranslationContext);

  useEffect(() => {
    // Detecta si la pantalla es móvil
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const pageSlug = language === 'es' ? slug : `${language}/${slug}`;
        const data = await getPageBySlug(pageSlug);

        // Si no hay contenido válido, redirigimos a 404
        if (!data || !data.content) {
          navigate('/404');
          return;
        }

        // Procesamos el contenido y reemplazamos shortcodes (incluyendo FAQs)
        const contentWithComponents = replaceShortcodesWithComponents(
          processHtmlContent(data.content, baseUrl)
        );

        // Extraemos metaTitle y metaDescription (o usamos valores por defecto)
        const metaTitle = (data.extras && data.extras.meta_title) || data.title;
        const metaDescription = (data.extras && data.extras.meta_description) || '';

        // Inyectamos el schema de Service basado en metaTitle y metaDescription
        let contentWithSchema = injectServiceSchema(contentWithComponents, metaTitle, metaDescription);

        // Obtenemos la URL completa de la página
        const pageUrl = `${baseUrl}${location.pathname}`;
        // Inyectamos adicionalmente el schema de WebPage
        contentWithSchema = injectWebPageSchema(contentWithSchema, metaTitle, metaDescription, pageUrl);

        setPage({ ...data, content: contentWithSchema });

        // Manejo de traducciones
        const translationsMap = {};
        if (language === 'es') {
          translationsMap['es'] = slug;
          if (data.translations && Array.isArray(data.translations)) {
            data.translations.forEach((t) => {
              const slugWithoutLang = t.slug.startsWith(`${t.locale}/`)
                ? t.slug.slice(t.locale.length + 1)
                : t.slug;
              translationsMap[t.locale] = slugWithoutLang;
            });
          }
        } else {
          translationsMap[language] = slug;
          if (data.main_page && data.main_page.slug) {
            translationsMap['es'] = data.main_page.slug;
          }
          if (data.main_page && data.main_page.translations && Array.isArray(data.main_page.translations)) {
            data.main_page.translations.forEach((t) => {
              const slugWithoutLang = t.slug.startsWith(`${t.locale}/`)
                ? t.slug.slice(t.locale.length + 1)
                : t.slug;
              translationsMap[t.locale] = slugWithoutLang;
            });
          }
          if (data.translations && Array.isArray(data.translations)) {
            data.translations.forEach((t) => {
              const slugWithoutLang = t.slug.startsWith(`${t.locale}/`)
                ? t.slug.slice(t.locale.length + 1)
                : t.slug;
              if (!translationsMap[t.locale]) {
                translationsMap[t.locale] = slugWithoutLang;
              }
            });
          }
        }
        setTranslations(translationsMap);
      } catch (error) {
        console.error('Error fetching page', error);
        navigate('/');
      }
    };
    fetchPage();
  }, [slug, baseUrl, language, setTranslations, navigate]);

  // Manejo de visibilidad de formularios al hacer clic en botones
  useEffect(() => {
    if (!page) return;
    const handleFormVisibility = () => {
      const buttons = document.querySelectorAll(".form1, .form2, .form3");
      if (buttons.length === 0) return;
      const divs = {
        form1: document.getElementById("form1"),
        form2: document.getElementById("form2"),
        form3: document.getElementById("form3"),
      };
      Object.values(divs).forEach((div) => {
        if (div) div.style.display = "none";
      });
      const handleClick = (event) => {
        const classList = Array.from(event.currentTarget.classList);
        const formClass = classList.find(cls => ["form1", "form2", "form3"].includes(cls));
        if (!formClass) return;
        Object.values(divs).forEach((div) => {
          if (div) div.style.display = "none";
        });
        if (divs[formClass]) {
          divs[formClass].style.display = "inline";
        }
      };
      buttons.forEach((button) => {
        button.addEventListener("click", handleClick);
      });
      return () => {
        buttons.forEach((button) => {
          button.removeEventListener("click", handleClick);
        });
      };
    };
    const cleanup = handleFormVisibility();
    return cleanup;
  }, [page]);

  // Calcular la URL canónica
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCanonicalUrl(window.location.origin + window.location.pathname);
    }
  }, []);

  if (!page) return null;

  const { title, extras } = page;
  const metaTitle = (extras && extras.meta_title) || title;
  const metaDescription = (extras && extras.meta_description) || '';
  const bgUrl = extras.bg_url ? `${baseUrl}/${extras.bg_url}` : getFallbackImage('bg');
  const imgUrl = extras.img_url ? `${baseUrl}/${extras.img_url}` : getFallbackImage('img');

 

  

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="preconnect" href={baseUrl} crossOrigin="true" />
        <link rel="preload" as="image" href={`${baseUrl}/images/logotipo_blanco.svg`} />
        {extras.img_url && <link rel="preload" as="image" href={imgUrl} />}
        {/* Agregamos la URL canónica */}
        {canonicalUrl && (
          <link rel="canonical" href={canonicalUrl} />
        )}
      </Helmet>

      <Header logoSrc="/images/logotipo_blanco.svg" />

      {!isMobile && (
        <BannerSectionStyle5
          bgColor="linear-gradient(40deg, #EE6FBB, #7BADD3)"
          imgUrl={imgUrl}
          title={title}
          subTitle={extras.subtitle}
        />
      )}

      {isMobile && (
        <div
          style={{
            background: 'linear-gradient(40deg, #EE6FBB, #7BADD3)',
            padding: '100px 0px',
            textAlign: 'center',
            borderBottomLeftRadius: '50px',
            borderBottomRightRadius: '50px',
          }}
        >
          <h1
            style={{
              color: '#ffffff',
              fontSize: '30px',
              marginBottom: '10px',
              marginTop: '30px',
              padding: '0px 20px',
            }}
          >
            {title}
          </h1>
          <p style={{ marginBottom: '10px', padding: '0px 20px' }}>{extras.subtitle}</p>
        </div>
      )}

      <Section className="section-loaded" topMd={60} bottomMd={200}>
        <div className="container container-services">
          <style>{extras.content_css}</style>
          {/* Reglas para que vídeos e iframes no desborden en móvil */}
          <style>{`
            .container-services iframe,
            .container-services video {
              max-width: 100%;
              height: 700px!important;
            }
          `}</style>
          {renderComponents(page.content)}
        </div>
      </Section>
      <ContactMethods />
    
    </>
  );
};

export default Services;
